import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { useAuthStore } from "@/auth/store";

interface CallbackSearch { token?: string }

export const Route = createFileRoute("/auth/callback")({
  validateSearch: (search): CallbackSearch => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: CallbackPage,
});

function CallbackPage() {
  const { token } = Route.useSearch();
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    if (!token) {
      setError("Missing token in callback URL.");
      return;
    }
    (async () => {
      try {
        const session = await authApi.callback(token);
        setSession(session);
        // If a pending invite token was stashed before sign-in, resume there.
        const pendingInvite = sessionStorage.getItem("tt-pending-invite-token");
        if (pendingInvite) {
          sessionStorage.removeItem("tt-pending-invite-token");
          navigate({ to: "/invite/$token", params: { token: pendingInvite }, replace: true });
        } else {
          navigate({ to: "/app", replace: true });
        }
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? err.message
            : "Sign-in failed. The link may have expired.";
        setError(msg);
      }
    })();
  }, [token, setSession, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="card w-full max-w-md text-center">
        {error ? (
          <>
            <h1 className="text-xl font-semibold text-red-700">Sign-in failed</h1>
            <p className="mt-2 text-sm text-ink/65">{error}</p>
            <a href="/login" className="btn-primary mt-6 inline-block">Back to sign in</a>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Signing you in…</h1>
            <p className="mt-2 text-sm text-ink/65">One moment.</p>
          </>
        )}
      </div>
    </div>
  );
}
